"use client"

import React, { useCallback, useEffect, useRef, useState } from "react"

/**
 * Shared resizable-drawer building blocks. Originally lived inside
 * related-requests-panel.tsx — extracted here so the VMS email preview (and
 * any future right-side panel) gets the exact same drag handle, snap-to-mode
 * behaviour, and visual treatment without copy-paste drift.
 *
 * Two pieces: the hook holds the resize state machine, the DragHandle is the
 * 18px-wide hit target that lives on the panel's left edge. Mount them
 * together — they're decoupled only so a panel can place the handle wherever
 * its layout needs it.
 */

interface UseResizableDrawerOptions {
  /** Width in px when collapsed. Snap point on drag end. */
  compactWidth: number
  /** Width in px when expanded. Snap point on drag end. */
  fullWidth: number
  /**
   * localStorage key for persisting the user's preferred mode across
   * sessions. Each panel uses its own key so VMS preview and Related
   * Requests don't share preferences — they have different ideal widths.
   * Pass `null` to opt out of persistence entirely.
   */
  storageKey?: string | null
  /**
   * Fraction of the compact↔full range a drag must cross to flip modes.
   * 0.42 ≈ "drag almost halfway". Short drags or clicks just toggle.
   */
  snapThresholdRatio?: number
}

type DrawerMode = "compact" | "full"

interface UseResizableDrawerResult {
  /** px width to apply to the panel right now (interpolated during drag). */
  currentWidth: number
  /** True while the user is actively dragging the handle. */
  dragging: boolean
  /** Mouse-down handler to wire onto the <DragHandle>. */
  onHandleMouseDown: (e: React.MouseEvent) => void
  /** Click-style toggle. Same effect as a click on the handle. */
  toggleMode: () => void
  /** Convenience: whether the drawer is currently in `full` mode. */
  isFull: boolean
}

export function useResizableDrawer({
  compactWidth,
  fullWidth,
  storageKey = null,
  snapThresholdRatio = 0.42,
}: UseResizableDrawerOptions): UseResizableDrawerResult {
  // Read the persisted mode lazily — useState init runs once.
  // localStorage may throw in sandboxed contexts (private mode, etc.) so
  // we soft-fail to "compact" in every error case.
  const readSavedMode = (): DrawerMode => {
    if (!storageKey) return "compact"
    try {
      return localStorage.getItem(storageKey) === "full" ? "full" : "compact"
    } catch {
      return "compact"
    }
  }

  const [mode, setMode] = useState<DrawerMode>(readSavedMode)
  const [dragging, setDragging] = useState(false)
  const [dragWidth, setDragWidth] = useState<number | null>(null)
  // Holds the mouse-down anchor: the cursor x at drag start, and the panel
  // width at that moment. Used to compute the live delta in onMove.
  const startRef = useRef<{ x: number; startWidth: number } | null>(null)

  const currentWidth = dragging && dragWidth !== null
    ? dragWidth
    : (mode === "full" ? fullWidth : compactWidth)

  const onHandleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    const startWidth = mode === "full" ? fullWidth : compactWidth
    startRef.current = { x: e.clientX, startWidth }
    setDragging(true)
    setDragWidth(startWidth)
  }, [mode, compactWidth, fullWidth])

  useEffect(() => {
    if (!dragging) return

    const onMove = (e: MouseEvent) => {
      if (!startRef.current) return
      // Panel is right-anchored, so a leftward drag (cursor x decreasing)
      // grows the width. Sign flip vs. a left-anchored drawer.
      const delta = startRef.current.x - e.clientX
      const raw = Math.min(fullWidth, Math.max(compactWidth, startRef.current.startWidth + delta))
      setDragWidth(raw)
    }

    const onUp = (e: MouseEvent) => {
      if (!startRef.current) return
      const delta = startRef.current.x - e.clientX
      const startedFull = startRef.current.startWidth === fullWidth

      // Tiny drag → treat as a click and just toggle. Big drag → decide by
      // which side of the snap-threshold the cursor ended up on.
      let nextMode: DrawerMode
      if (Math.abs(delta) < 5) {
        nextMode = startedFull ? "compact" : "full"
      } else {
        const range = fullWidth - compactWidth
        const threshold = range * snapThresholdRatio
        if (startedFull) {
          nextMode = delta < -threshold ? "compact" : "full"
        } else {
          nextMode = delta > threshold ? "full" : "compact"
        }
      }

      setMode(nextMode)
      if (storageKey) {
        try { localStorage.setItem(storageKey, nextMode) } catch { /* ignore */ }
      }
      setDragging(false)
      setDragWidth(null)
      startRef.current = null
    }

    window.addEventListener("mousemove", onMove)
    window.addEventListener("mouseup", onUp)
    return () => {
      window.removeEventListener("mousemove", onMove)
      window.removeEventListener("mouseup", onUp)
    }
  }, [dragging, compactWidth, fullWidth, snapThresholdRatio, storageKey])

  const toggleMode = useCallback(() => {
    setMode(m => {
      const next: DrawerMode = m === "full" ? "compact" : "full"
      if (storageKey) {
        try { localStorage.setItem(storageKey, next) } catch { /* ignore */ }
      }
      return next
    })
  }, [storageKey])

  return { currentWidth, dragging, onHandleMouseDown, toggleMode, isFull: mode === "full" }
}

interface DragHandleProps {
  onMouseDown: (e: React.MouseEvent) => void
  onToggle: () => void
  isFull: boolean
  /** Accessible label for screen readers + tooltip. Falls back to a generic. */
  expandLabel?: string
  collapseLabel?: string
}

/**
 * 18×64 hit target on the left edge of a right-anchored drawer. Click to
 * toggle, drag to resize. Visually a small tab with five dots and a chevron;
 * brightens on hover. Lives at `left: -18` so it sticks out of the panel
 * by 14px of visible width.
 */
export function DragHandle({
  onMouseDown,
  onToggle,
  isFull,
  expandLabel,
  collapseLabel,
}: DragHandleProps) {
  const [hovered, setHovered] = useState(false)
  const label = isFull
    ? (collapseLabel || 'Collapse panel')
    : (expandLabel || 'Expand panel')

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={label}
      aria-expanded={isFull}
      onMouseDown={onMouseDown}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setHovered(true)}
      onBlur={() => setHovered(false)}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onToggle()
        }
      }}
      title={label}
      style={{
        position: "absolute",
        left: -18,
        top: "50%",
        transform: "translateY(-50%)",
        width: 18,
        height: 64,
        cursor: "pointer",
        zIndex: 10,
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        outline: "none",
      }}
    >
      <div style={{
        width: 14,
        height: 64,
        borderRadius: "6px 0 0 6px",
        backgroundColor: hovered ? "#3BD4AE" : "#E2E8F0",
        border: hovered ? "none" : "1px solid #CBD5E1",
        borderRight: "none",
        transition: "background-color 0.15s ease, border-color 0.15s ease",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 3,
        boxShadow: hovered ? "-2px 0 8px rgba(59,212,174,0.25)" : "-1px 0 4px rgba(0,0,0,0.06)",
      }}>
        <span style={{
          fontSize: 9,
          lineHeight: 1,
          color: hovered ? "#FFFFFF" : "#94A3B8",
          fontWeight: 700,
          letterSpacing: -1,
          userSelect: "none",
        }}>
          {isFull ? "»" : "«"}
        </span>
        {[0, 1, 2, 3, 4].map(i => (
          <span key={i} style={{
            width: 3,
            height: 3,
            borderRadius: "50%",
            backgroundColor: hovered ? "rgba(255,255,255,0.7)" : "#B0BEC5",
            flexShrink: 0,
          }} />
        ))}
        <span style={{
          fontSize: 9,
          lineHeight: 1,
          color: hovered ? "#FFFFFF" : "#94A3B8",
          fontWeight: 700,
          letterSpacing: -1,
          userSelect: "none",
        }}>
          {isFull ? "»" : "«"}
        </span>
      </div>
    </div>
  )
}
